// src/hooks/useIdleLogout.js
import { useEffect, useRef } from "react";
import { performLogout } from "utils/logout";

/**
 * Logs the user out after `timeoutMs` of no activity.
 * Activity = mouse, keyboard, touch, scroll, focus.
 */
export default function useIdleLogout({ timeoutMs = 20 * 60 * 1000, dispatch, navigate } = {}) {
  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);

    timerRef.current = window.setTimeout(() => {
      // Only logout if user is actually logged in
      const token = localStorage.getItem("token");
      if (!token) return;

      performLogout({
        dispatch,
        navigate,
        reason: "You were logged out due to 20 minutes of inactivity.",
      });
    }, timeoutMs);
  };

  useEffect(() => {
    // Start timer immediately when hook mounts
    resetTimer();

    // Events that count as activity
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
      "focus",
    ];

    const handleActivity = () => resetTimer();

    events.forEach((event) => window.addEventListener(event, handleActivity, { passive: true }));

    // If user switches tabs and comes back, count as activity
    const handleVisibility = () => {
      if (!document.hidden) resetTimer();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      events.forEach((event) => window.removeEventListener(event, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutMs]);
}